<template>
  <div v-if="showSection" class="space-y-3">
    <Divider />
    <h2>{{ t('title') }}</h2>
    <p class="text-sm text-gray-600">{{ t('description') }}</p>
    <p v-if="!items.length" class="text-sm text-gray-500">{{ t('empty') }}</p>
    <ul v-else class="space-y-3">
      <li
        v-for="item in items"
        :key="item.id"
        class="flex flex-wrap items-center justify-between gap-2 rounded-md border border-obsidian-100 p-3"
      >
        <div class="min-w-0">
          <div class="truncate font-medium">{{ item.damage?.description ?? item.name }}</div>
          <div class="text-xs text-gray-500">
            {{ t(KIND_LABELS[item.kind]) }}
            · {{ formatSize(item.blob.size) }}
          </div>
          <div v-if="item.lastError" class="text-xs text-red-600">{{ item.lastError }}</div>
        </div>
        <div class="flex gap-2">
          <CVButton size="sm" variant="secondary" @click="handleRetry(item.id)">
            {{ t('retry') }}
          </CVButton>
          <CVButton size="sm" variant="ghost" @click="handleExport(item.id)">
            {{ t('export') }}
          </CVButton>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import CVButton from '@/components/buttons/CVButton.vue';
import Divider from '@/components/Divider.vue';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useEvidenceUploadQueue } from '@/hooks/use-evidence-upload-queue';
import { DAMAGE_IN_APP_EVIDENCE_FLAG } from '@/utils/damage-evidence';
import { evidenceUploadQueue, type EvidenceKind } from '@/utils/evidence-upload-queue';
import { VEHICLE_INSPECTION_FLAG } from '@/components/inspections/inspection-state';

const { t } = useI18n();
const damageFlagOn = useFeatureFlag(DAMAGE_IN_APP_EVIDENCE_FLAG);
const inspectionFlagOn = useFeatureFlag(VEHICLE_INSPECTION_FLAG);
const { items } = useEvidenceUploadQueue();
const showSection = computed(
  () => damageFlagOn.value || inspectionFlagOn.value || items.value.length > 0,
);

const KIND_LABELS: Record<EvidenceKind, string> = {
  photo: 'photo',
  video: 'video',
  'inspection-shot': 'inspectionShot',
  damage: 'unsentDamage',
};

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const handleRetry = (id: string) => {
  void evidenceUploadQueue.retry(id);
};

const handleExport = async (id: string) => {
  const file = await evidenceUploadQueue.exportItem(id);
  if (!file) return;
  const shareData = { files: [file] };
  if (navigator.share && navigator.canShare?.(shareData)) {
    await navigator.share(shareData);
    return;
  }
  const url = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.name;
  link.click();
  URL.revokeObjectURL(url);
};
</script>

<i18n lang="json">
{
  "en": {
    "title": "Photo and video uploads",
    "description": "Damage evidence and vehicle photos on this device that have not been attached yet. Uploads retry while ROS is open.",
    "empty": "Nothing waiting to upload.",
    "photo": "Photo",
    "video": "Video",
    "inspectionShot": "Vehicle photo",
    "unsentDamage": "Damage not sent yet",
    "retry": "Retry",
    "export": "Export"
  },
  "de": {
    "title": "Foto- und Video-Uploads",
    "description": "Schadensbelege und Fahrzeugfotos auf diesem Gerät, die noch nicht angehängt sind. Uploads wiederholen sich, solange ROS offen ist.",
    "empty": "Nichts wartet auf den Upload.",
    "photo": "Foto",
    "video": "Video",
    "inspectionShot": "Fahrzeugfoto",
    "unsentDamage": "Schaden, noch nicht gesendet",
    "retry": "Erneut",
    "export": "Exportieren"
  },
  "es": {
    "title": "Cargas de fotos y vídeos",
    "description": "Evidencias de daños y fotos del vehículo en este dispositivo que aún no se han adjuntado. Se reintenta mientras ROS está abierto.",
    "empty": "Nada pendiente de subir.",
    "photo": "Foto",
    "video": "Vídeo",
    "inspectionShot": "Foto del vehículo",
    "unsentDamage": "Daño aún no enviado",
    "retry": "Reintentar",
    "export": "Exportar"
  }
}
</i18n>
